import { makeComputationApiRepository } from "../repositories/repositoryBuilders.js";

/**
 * Provide a user-friendly way to reach the forecast editor for the
 * main template depending on which state the company is in:
 *
 * - If the company has already a main forecast template, just
 *   redirect the user to the forecast editor for this template.
 *
 * - If the company has no main forecast template yet but has a
 *  default forecast template configuration, create a new forecast
 *  template (which should automatically become the main one) and
 *  redirect the user to the forecast editor.
 *
 * - If the company has no default forecast template configuration,
 *   redirect the user to the forecast onboarding wizard.
 */
export default class MainForecastTemplateService {
	constructor({ session, router }) {
		this._session = session;
		this._router = router;

		this._configurationRepository = makeComputationApiRepository({
			route: "/DefaultForecastTemplateConfiguration",
			session,
		});

		this._forecastTemplateRepository = makeComputationApiRepository({
			route: "/ForecastTemplates/?id",
			session,
		});
	}

	async gotoForecastOnboardingOrMainForecastTemplateEditor() {
		return (await this._hasMainForecastTemplate())
			? this._gotoMainForecastTemplateEditor()
			: this._gotoForecastOnboarding();
	}

	_hasMainForecastTemplate() {
		return this._configurationRepository.head();
	}

	async _gotoMainForecastTemplateEditor() {
		const forecastTemplateId = await this._getOrMakeMainForecastTemplate();

		this._router.redirectTo("forecast-editor/#forecastTemplateId", {
			forecastTemplateId,
		});
	}

	_gotoForecastOnboarding() {
		this._router.redirectTo("forecast-onboarding");
	}

	async _getOrMakeMainForecastTemplate() {
		let forecastTemplateData;

		try {
			forecastTemplateData = await this._forecastTemplateRepository.get({
				filter: { id: "Main" },
				notify: false,
			});
		} catch (error) {
			if (!error.isRepositoryError || error.status !== 404) {
				throw error;
			}

			forecastTemplateData = await this._forecastTemplateRepository.save({
				model: {
					chartOfAccountsId:
						this._session.getCurrentChartOfAccountsId(),
				},
			});
		}

		return forecastTemplateData.id;
	}
}
